import React, { useCallback } from 'react';
import { Box, Preloader } from '@common-fe/common-fe';

import { ClaimSelectPlace } from '@/modules/employer/components/Lockbox/lockbox.types';
import { LoadNextItemsButton, MatchingListEmptyState } from '@/modules/employer/components/Lockbox/LockboxDetails/components/LockboxInfo/LockboxMatchingList';
import useClaimsCardsList from '@/modules/employer/components/Lockbox/LockboxDetails/hooks/useClaimsCardsList';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';
import { useGetClaimsRepaymentMatchesQuery } from '@/modules/employer/components/Lockbox/queries/useGetClaimsRepaymentMatches.query';
import usePagination from '@/utils/hooks/usePagination';

import ClaimsList from './components/ClaimsList';
import ToggleList from './components/ToggleSection';
import useModifyClaimRepayments from './hooks/useModifyClaimRepayments';

const AllEmployeeClaims = () => {
  const { originalLockbox, editableLockbox, isEditMode } = useLockboxDetailsStore();
  const { claimsRepaymentMatches, isLoading } = useGetClaimsRepaymentMatchesQuery({ searchString: originalLockbox?.employeeName, page: 0, perPage: 50 });
  const { fields } = useClaimsCardsList({ lockboxClaims: claimsRepaymentMatches });
  const { slicedItems, hasNextItems, handleNext } = usePagination({ items: fields, initialSize: 4 });
  const { toggleClaim } = useModifyClaimRepayments();

  const handleToggleClaim = useCallback((claimId?: string) => {
    const claimData = claimsRepaymentMatches?.find((claim) => claim?.claimId && claim?.claimId === claimId);
    toggleClaim(claimId, ClaimSelectPlace.ALL_CLAIMS, claimData);
  }, [toggleClaim, claimsRepaymentMatches]);

  return (
    <Box>
      <ToggleList testId="all-employee-claims" title="Show all claims of this employee">
        <Box>
          <ClaimsList
            fields={slicedItems}
            isEditMode={isEditMode}
            selectedClaims={editableLockbox?.claimRepayments}
            onSelectClaim={handleToggleClaim}
            emptyState={(
              <Box margin={{ top: 'spacing8' }}>
                {isLoading ? (
                  <Preloader testId="claims-search_preloader" />
                ) : (
                  <MatchingListEmptyState title="No Search Results" testId="all-lockbox-claims" />
                )}
              </Box>
            )}
            selectPlace={ClaimSelectPlace.ALL_CLAIMS}
            showWarnings
          />

          {!!slicedItems?.length && hasNextItems && (
            <Box margin={{ top: 'spacing8' }} align="center">
              <LoadNextItemsButton
                testId="suggested-lockbox-claims"
                hasNextItems={hasNextItems}
                onClick={handleNext}
              />
            </Box>
          )}
        </Box>
      </ToggleList>
    </Box>
  );
};

export default AllEmployeeClaims;
